import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Calendar, Clock, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';

interface PostData {
  title: string;
  content: string;
  date: string;
  category: string;
  image: string;
  readTime: string;
}

const formatContent = (content: string) => {
  if (!content) return '';

  // Improved line-by-line parsing
  const lines = content.split('\n');
  let isInList = false;
  const processedLines: string[] = [];

  const headers = [
    'KEY OBSERVATIONS:',
    'TECHNICAL CONFLUENCE:',
    'UPSIDE TARGETS:',
    'RISK MANAGEMENT:',
    'CONCLUSION:'
  ];

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      if (isInList) {
        processedLines.push('</ul>');
        isInList = false;
      }
      return;
    }

    // 1. Check if line is a header
    const isHeader = headers.some(h => trimmed.toUpperCase().includes(h));

    if (isHeader) {
      if (isInList) {
        processedLines.push('</ul>');
        isInList = false;
      }
      processedLines.push(`<h2 class="text-xl font-semibold mt-10 mb-4 text-[#0B1F3A] font-heading uppercase tracking-wide">${trimmed}</h2>`);
      return;
    }

    // 2. Check if line is a bullet point
    if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
      const bulletContent = trimmed.substring(1).trim();
      if (!isInList) {
        processedLines.push('<ul class="list-disc pl-6 space-y-2 text-slate-700 leading-7 my-6">');
        isInList = true;
      }
      processedLines.push(`<li>${bulletContent}</li>`);
      return;
    }

    // 3. Regular paragraph
    if (isInList) {
      processedLines.push('</ul>');
      isInList = false;
    }
    processedLines.push(`<p class="text-base md:text-lg text-slate-700 font-medium leading-8 tracking-wide mb-8">${trimmed}</p>`);
  });

  if (isInList) {
    processedLines.push('</ul>');
  }

  return processedLines.join('');
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState<PostData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);

      const data = await safeFetch<any>(
        supabase
          .from('blog_posts')
          .select('*')
          .eq('slug', slug)
          .single(),
        null,
        'Loading research report'
      );

      if (data) {
        setPost({
          title: data.title,
          content: data.content,
          date: data.created_at,
          category: data.tags?.split(',')[0] || 'Strategic Insight',
          image: data.thumbnail || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&auto=format&fit=crop&q=80',
          readTime: `${Math.max(1, Math.ceil((data.content?.split(' ').length || 0) / 200))} min read`
        });
      } else {
        setPost(null); // Ensure post is null if not found
      }
      setIsLoading(false);
    };

    fetchPost();
  }, [slug]);

  if (isLoading) {
    return (
      <Layout>
        <div className="section-padding container-max text-center flex flex-col items-center justify-center py-40 gap-6">
          <Loader2 className="w-12 h-12 animate-spin text-accent" />
          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Accessing Encrypted Archives...</p>
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="section-padding container-max text-center">
          <h1 className="heading-lg text-primary mb-4">Post Not Found</h1>
          <p className="text-muted-foreground mb-8">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="btn-navy">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Header */}
      <section className="relative pt-12 pb-8 bg-background overflow-hidden text-center">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-[120px] z-0" />

        <div className="container-max relative z-10 px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-primary/40 hover:text-accent mb-12 transition-all group">
            <div className="w-8 h-8 rounded-full bg-primary/5 flex items-center justify-center group-hover:bg-accent group-hover:text-accent-foreground transition-all">
              <ArrowLeft className="w-3 h-3 group-hover:-translate-x-0.5 transition-transform" />
            </div>
            Back to Journal
          </Link>

          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#0B1F3A] tracking-tight leading-snug mb-8">
              {post.title}
            </h1>

            <div className="flex items-center justify-center gap-6 mb-8">
              <span className="px-4 py-1.5 bg-accent/10 rounded-full text-[10px] font-black uppercase tracking-widest text-accent">
                {post.category}
              </span>
              <div className="h-px w-8 bg-gray-200" />
              <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest leading-none pt-0.5">{post.readTime}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Compact Hero Image */}
      <section className="container-max px-6 mb-8 relative z-20">
        <div className="max-w-4xl mx-auto h-[250px] md:h-[350px] overflow-hidden rounded-2xl shadow-md border border-white/60">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Metadata & Content */}
      <section className="pb-32 bg-background">
        <div className="container-max max-w-3xl px-6">
          {/* Metadata Bar */}
          <div className="flex flex-wrap items-center justify-center gap-8 py-8 border-y border-gray-100 mb-16">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/5 flex items-center justify-center border border-primary/10">
                <Calendar className="w-5 h-5 text-primary/40" />
              </div>
              <div className="flex flex-col text-left">
                <span className="text-[8px] font-black uppercase tracking-widest text-slate-400 mb-0.5">PUBLISHED</span>
                <span className="text-[11px] font-bold text-primary/60 uppercase tracking-widest">
                  {new Date(post.date).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/5 flex items-center justify-center border border-primary/10">
                <Clock className="w-5 h-5 text-primary/40" />
              </div>
              <div className="flex flex-col text-left">
                <span className="text-[8px] font-black uppercase tracking-widest text-slate-400 mb-0.5">TYPE</span>
                <span className="text-[11px] font-bold text-primary/60 uppercase tracking-widest">STRATEGIC INTEL</span>
              </div>
            </div>
          </div>

          <article
            className="prose prose-lg max-w-none 
              prose-headings:font-heading prose-p:font-sans
              space-y-6"
            dangerouslySetInnerHTML={{ __html: formatContent(post.content) }}
          />

          {/* Institutional CTA */}
          <div className="mt-32 relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-navy-dark to-primary p-10 md:p-14 text-center shadow-premium">
            <div className="absolute inset-0 opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
            <div className="relative z-10 max-w-2xl mx-auto">
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-accent mb-6 block">Direct Access</span>
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-white tracking-tight mb-8 leading-tight uppercase">Begin Your Personal <br />Mastery Journey</h3>
              <p className="text-white/60 text-sm font-medium mb-10 leading-relaxed max-w-md mx-auto">Secure your position in our elite training collective and master the mechanics of institutional trading.</p>
              <Link to="/courses">
                <Button className="btn-gold h-14 px-10 text-[10px] font-black uppercase tracking-widest shadow-2xl">
                  Explore Mastery Programs
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogPost;
